// Pure logic for the Generate view (docs/plan/phase-10-ui.md): client-side
// task_id/seed generation, the photo x variant result count, a
// stale-response-safe quote reducer, and the upload plan/sequencing the
// "Run" button drives. Kept framework-free and exported individually so
// each piece is unit-testable without rendering the component
// (docs/plan/phase-10-ui.md "UI tests").
import type { Quote } from "../api/client";

// ---- ids / seeds --------------------------------------------------------

/** RFC 4122 v4 UUID, as Core requires for the client-generated task_id
 * (02-core.md #3). Prefers the Web Crypto API; falls back to Math.random
 * only where randomUUID is unavailable. */
export function newTaskId(): string {
  const g = globalThis as { crypto?: Crypto };
  if (g.crypto && typeof g.crypto.randomUUID === "function") {
    return g.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** A single random seed in fields.json's `seed` range (integer, 0..2^32-1). */
export function randomSeed(): number {
  const g = globalThis as { crypto?: Crypto };
  if (g.crypto && typeof g.crypto.getRandomValues === "function") {
    const buf = new Uint32Array(1);
    g.crypto.getRandomValues(buf);
    return buf[0];
  }
  return Math.floor(Math.random() * 4294967296);
}

/** One seed per variant (ADR-0008: the client expands the variants slider
 * into N concrete seeds before submission; the seed array is shared
 * across every photo via the Cartesian product, not one array per photo). */
export function generateVariantSeeds(variantCount: number): number[] {
  if (!Number.isInteger(variantCount) || variantCount < 1) {
    throw new Error("variantCount must be a positive integer");
  }
  return Array.from({ length: variantCount }, () => randomSeed());
}

// ---- result count / quote ------------------------------------------------

/** image x seed Cartesian product size (02-core.md #4). */
// Clip lengths and frame rates the photo-to-video grades offer, as the LTX
// API's fast tier does (ADR-0041): 5/10/20 s at 24 fps, 48 fps up to 10 s;
// Core refuses a combination a grade cannot serve.
export const CLIP_SECONDS = [5, 10, 20] as const;
export const CLIP_FPS = [24, 48] as const;

// clipFrames is a length in seconds as DFR frames: 24 per second on the
// 24 fps base plus the closing frame (5 s -> 121, 10 s -> 241, 20 s -> 481).
export function clipFrames(seconds: number): number {
  return seconds * 24 + 1;
}

/** Where the photo sits in the clip (ADR-0059): the first frame (the usual
 * image-to-video), a middle frame, or the last frame (the clip arrives at it). */
export type PhotoAt = "start" | "middle" | "end";

/** The `image_frame` input for a placement: none for the first frame, -1 for
 * the last, the middle frame of a clip of `seconds` (the engine moves it to
 * the nearest 8th frame). */
export function imageFrameFor(at: PhotoAt, seconds: number): number | undefined {
  if (at === "end") return -1;
  if (at === "middle") return Math.floor((clipFrames(seconds) - 1) / 2);
  return undefined;
}

export function resultCount(photoCount: number, variantCount: number): number {
  return Math.max(photoCount, 0) * Math.max(variantCount, 0);
}

export interface QuoteState {
  results: number;
  quote?: Quote;
  /** true once `results` has changed since `quote` was fetched for it. */
  stale: boolean;
}

export function initialQuoteState(results: number): QuoteState {
  return { results, quote: undefined, stale: results > 0 };
}

/** Call when photoCount/variantCount change. Keeps the last quote on
 * screen (still useful context) but marks it stale so the UI can show
 * "updating..." instead of a wrong number, and so a response for the
 * *previous* results value can be told apart from the one that matters now. */
export function onResultsChanged(state: QuoteState, newResults: number): QuoteState {
  if (newResults === state.results) return state;
  return { results: newResults, quote: state.quote, stale: newResults > 0 };
}

/** Call when a quote request resolves. Ignored if `results` has moved on
 * since the request was issued (a slow response for a stale variant
 * count must never overwrite a newer one -- classic race condition). */
export function onQuoteLoaded(state: QuoteState, forResults: number, quote: Quote): QuoteState {
  if (forResults !== state.results) return state;
  return { results: state.results, quote, stale: false };
}

// ---- upload plan / sequencing --------------------------------------------

export type UploadStatus = "pending" | "uploading" | "done" | "error";

export interface UploadStep {
  index: number;
  file: File;
  status: UploadStatus;
  artifactId?: string;
  error?: string;
}

export function initialUploadPlan(files: File[]): UploadStep[] {
  return files.map((file, index) => ({ index, file, status: "pending" as const }));
}

/** The next step that still needs an attempt: "pending" (never tried) or
 * "error" (retry), in file order -- never "uploading" (already in
 * flight) or "done". Uploads run strictly one at a time in this order so
 * progress is predictable; re-running the same File list after an
 * interruption resumes here instead of restarting everything (Core's
 * allocate-then-PUT is itself safe to repeat, 08-contracts.md #7, so a
 * needless repeat of an already-committed step is also harmless -- this
 * just avoids doing it). */
export function nextPendingUpload(steps: UploadStep[]): UploadStep | undefined {
  return steps.find((s) => s.status === "pending" || s.status === "error");
}

export function allUploadsDone(steps: UploadStep[]): boolean {
  return steps.length > 0 && steps.every((s) => s.status === "done");
}

export function setStepStatus(steps: UploadStep[], index: number, patch: Partial<UploadStep>): UploadStep[] {
  return steps.map((s) => (s.index === index ? { ...s, ...patch } : s));
}

/** Ordered artifact ids once every step is done, for the `image` field of
 * POST /tasks (08-contracts.md #1: "the client ... uploads each photo via
 * POST /artifacts then PUT to Store ..., then POST /tasks with image
 * array and seed array"). Throws if any step isn't done, so a caller
 * can't accidentally submit a Task with a missing slot. */
export function committedArtifactIds(steps: UploadStep[]): string[] {
  return steps
    .slice()
    .sort((a, b) => a.index - b.index)
    .map((s) => {
      if (s.status !== "done" || !s.artifactId) {
        throw new Error(`upload step ${s.index} is not done`);
      }
      return s.artifactId;
    });
}
